import React, { useEffect, useCallback } from "react";
import PropTypes from "kf-proptypes/main";
import { t } from "@lingui/macro";
import { Redirect, Route, useHistory, useRouteMatch } from "react-router-dom";

import { useUserStore, useAccountStore } from "base/stores";
import { pushNotificationClient } from "base/pushnotification";
import { rtdbClient } from "base/rtdb";
import { Spinner, SPINNER_SIZES } from "widgets/spinner";
import { Button } from "widgets/inputs";
import { authenticateSandbox } from "account/singlesignon";
import { Alert } from "widgets/feedback";
import { DataService, Interceptor } from "base/services";
import { AccountErrors } from "base/constants";
import { SandboxUserNotAvailable } from "account/notavailable";
import { canAuthenticateSandbox } from "base/utils";

import { Layout } from "../layout/index.jsx";

import styles from "./base.css";

Authendication.propTypes = {
  children: PropTypes.node
};
function Authendication({ children }) {
  let isAuthendicated = useUserStore((state) => state.isAuthendicated);

  let history = useHistory();
  let match = useRouteMatch("/");

  const tryRedirectToActualURLBeforeSignIn = useCallback(
    function tryRedirectToActualURLBeforeSignIn() {
      let redirectURL = localStorage.getItem("redirectURL");
      if (redirectURL) {
        // I don't know the reason behind this setTimeout, am just moving this code here.
        setTimeout(() => {
          // NOTE: Internal route basename has "view", Need to clean "view", since internal private route redirecton (<PrivateRoute>)
          // wont work for main kissflow application but it will work for portal and appbuilder, since backend route support is added only for appbuilder and portal
          if (redirectURL.startsWith("/view/")) {
            redirectURL = redirectURL.replace("/view/", "/");
          }
          // NOTE: All portal routes have a base name of "portal"
          if (redirectURL.startsWith("/portal/")) {
            redirectURL = redirectURL.replace("/portal/", "/");
          }
          history.push(redirectURL);
          localStorage.removeItem("redirectURL");
        }, 0);
      }
    },
    [history]
  );
  useEffect(
    function tryRedirect() {
      if (isAuthendicated) tryRedirectToActualURLBeforeSignIn();
    },
    [tryRedirectToActualURLBeforeSignIn, isAuthendicated]
  );

  if (isAuthendicated === null) {
    return (
      <div className={styles.loaderBody}>
        <Spinner size={SPINNER_SIZES.MEDIUM} />
      </div>
    );
  } else if (isAuthendicated === false) {
    const { pathname, search = "" } = window.location;
    localStorage.setItem("redirectURL", pathname + search);

    /** */
    if (canAuthenticateSandbox()) {
      return authenticateSandbox();
    }
    /** */

    history.push(`${match.url}login`);
    return "";
  } else if (isAuthendicated) {
    localStorage.setItem("isSessionExpired", false);
    return children;
  }
}

export function PublicRoute(props) {
  return <Route {...props} />;
}

export function PrivateRoute({ children, baseRedirect = false, ...rest }) {
  return (
    <Route
      {...rest}
      render={({ location }) => {
        let path = location.pathname;

        // NOTE: Internal redirection has "/view", below sinppet is needed to redirect to the respective basename
        // After making the proper changes in backend and internal code, below snippet will be removed
        if (baseRedirect && path.startsWith("/view/")) {
          path = path.replace("/view/", "/");

          return <Redirect to={path} />;
        }

        return children;
      }}
    />
  );
}

PrivateRoute.propTypes = {
  children: PropTypes.node.isRequired,
  baseRedirect: PropTypes.bool
};

LoginRoute.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Note : This logic should move to server in future.
 */
export function LoginRoute({ children, ...rest }) {
  return (
    <Route
      {...rest}
      render={() => {
        if (canAuthenticateSandbox()) {
          /**
           * Note : Login route is not applicable for sandbox accounts
           */
          return <ParentAccountRedirect />;
        }

        return children;
      }}
    />
  );
}

export function AuthorizedComponents(props) {
  let history = useHistory();

  useEffect(function addingInterceptor() {
    //add interceptor
    Interceptor.addInterceptor({
      responseError: (error) => {
        return handleAccountErrorRedirections(error);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function tabListener() {
    let isAlertVisible = false;
    let destroyRef = null;
    function onFocus() {
      if (document.visibilityState === "visible") {
        if (localStorage.getItem("isSessionExpired") === "true") {
          /** */
          if (canAuthenticateSandbox()) {
            return authenticateSandbox();
          }
          /** */

          if (isAlertVisible) return;
          isAlertVisible = true;
          Alert.showAlert({
            title: t`You have been signed out`,
            onOK: () => window.location.reload(),
            content: t`Please sign in again to continue using Kissflow`,
            closable: false,
            closeOnOutsideClick: false,
            closeOnEscape: false,
            footer: ({ destroy }) => {
              destroyRef = destroy;
              return (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    onClick={() => {
                      window.location.reload();
                    }}
                  >
                    {t`Sign in`}
                  </Button>
                </div>
              );
            }
          });
        } else if (localStorage.getItem("isSessionExpired") === "false") {
          destroyRef && destroyRef();
          isAlertVisible = false;

          let [pingRequest] = DataService.post(
            `/global/2/${useAccountStore.getState()._id}/ping`,
            {
              toast: false
            }
          );

          pingRequest().catch((err) => {
            if (
              err.statusCode === 403 &&
              err.error_code === "KISSFLOW_ERROR_00031"
            ) {
              /** */
              if (canAuthenticateSandbox()) {
                return authenticateSandbox();
              }
              /** */
              window.location.reload();
            }
          });
        }
      }
    }
    window.addEventListener("visibilitychange", onFocus);
    return function unMount() {
      window.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  function handleAccountErrorRedirections(error) {
    if (
      error &&
      error.statusCode === 403 &&
      error.type === AccountErrors.TrialExpired
    ) {
      history.push("/trialexpired");
    } else if (error && error.type === AccountErrors.DunningExpired) {
      history.push("/dunning");
    }
    if (
      error?.statusCode === 401 &&
      error?.error_code === "KISSFLOW_ERROR_00014" &&
      localStorage.getItem("isSessionExpired") === "false"
    ) {
      pushNotificationClient.signout();
      rtdbClient.signout();
      /** */
      if (canAuthenticateSandbox()) {
        return authenticateSandbox();
      }
      /** */
      window.location.reload();
    }
    return Promise.reject(error);
  }

  return (
    <Authendication>
      <Layout {...props} />
    </Authendication>
  );
}

export function ParentAccountRedirect() {
  let isAuthendicated = useUserStore((state) => state.isAuthendicated);

  let url = new URL(window.location.href);
  let loginError = url.searchParams.get("e") || "";

  if (loginError) {
    let errorArgs = url.searchParams.get("args") || "";
    return <SandboxUserNotAvailable error={loginError} args={errorArgs} />;
  }

  if (isAuthendicated === null) {
    return null;
  }

  if (isAuthendicated) {
    window.location.replace("/view/home");
    return null;
  }

  if (canAuthenticateSandbox()) {
    return authenticateSandbox(true);
  }

  return null;
}
